import { getLanguage, type Language, type TranslationValues } from './i18n';

export class ApiError extends Error {
  constructor(public status: number, message: string, public code?: string, public errors?: unknown, public messageSource?: string, public messageParameters?: TranslationValues, public messageTranslations?: Partial<Record<Language, string>>) {
    super(message); this.name = 'ApiError';
  }
}

export type Session = { authenticated: boolean; user?: { code: string; name: string; limited: boolean }; csrfToken?: string };
let csrfToken: string | undefined;
let sessionRequest: Promise<Session> | undefined;

export function newRequestId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0,8)}-${hex.slice(8,12)}-${hex.slice(12,16)}-${hex.slice(16,20)}-${hex.slice(20)}`;
}

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  if (!path.startsWith('/api/')) throw new Error('API-Pfade müssen mit /api/ beginnen.');
  const method = (options.method ?? 'GET').toUpperCase();
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method) && !csrfToken) {
    sessionRequest ??= api<Session>('/api/auth/session').finally(() => { sessionRequest = undefined; });
    await sessionRequest;
  }
  const headers = new Headers(options.headers);
  headers.set('Accept', 'application/json');
  headers.set('Accept-Language', getLanguage());
  if (typeof options.body === 'string' && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  if (csrfToken && !['GET', 'HEAD', 'OPTIONS'].includes(method)) headers.set('X-CSRF-TOKEN', csrfToken);
  const response = await fetch(import.meta.env.BASE_URL.replace(/\/$/, '') + path, { ...options, method, headers, credentials: 'same-origin' });
  const body: unknown = response.status === 204 ? undefined : response.headers.get('content-type')?.includes('json') ? await response.json() : undefined;
  if (!response.ok) {
    const detail = body as { message?: string; code?: string; errors?: unknown; messageSource?: string; messageParameters?: TranslationValues; messageTranslations?: Partial<Record<Language, string>> } | undefined;
    if (response.status === 401 && !path.startsWith('/api/auth/')) {
      csrfToken = undefined;
      window.dispatchEvent(new Event('kombinas:session-expired'));
    }
    throw new ApiError(response.status, detail?.message ?? `Anfrage fehlgeschlagen (${response.status}).`, detail?.code, detail?.errors,
      detail?.messageSource ?? (detail?.message ? undefined : 'Anfrage fehlgeschlagen ({status}).'), detail?.messageParameters ?? (detail?.message ? undefined : { status: response.status }), detail?.messageTranslations);
  }
  if (path === '/api/auth/session' || path === '/api/auth/login') csrfToken = (body as Session | undefined)?.csrfToken ?? csrfToken;
  if (path === '/api/auth/logout') csrfToken = undefined;
  return body as T;
}

